import { useMemo } from 'react';

interface UseFilteredDataProps {
  data: any[];
  searchTerm: string;
  isMatrix: boolean;
  nonSearchableColumns?: string[];
  searchOnlyPrincipalData?: boolean;
  childrenColumnName?: string;
}

const useFilteredData = ({
  data,
  searchTerm,
  isMatrix,
  nonSearchableColumns,
  searchOnlyPrincipalData = false,
  childrenColumnName = 'children',
}: UseFilteredDataProps): any[] => {
  return useMemo(() => {
    if (!data || !Array.isArray(data) || !searchTerm?.trim()) {
      return data; // Return original data if invalid input or searchTerm is empty
    }

    const searchTermLower = searchTerm.trim().toLowerCase();
    const excludedProperties = isMatrix
      ? [
          'locations',
          'id',
          'uuid',
          'key',
          '__typename',
          ...(nonSearchableColumns ?? []),
        ]
      : ['uuid', 'id', 'key', '__typename', ...(nonSearchableColumns ?? [])];

    const searchInValue = (value: any, isSearchingChildren: boolean = false): boolean => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') {
        return value.trim().toLowerCase().includes(searchTermLower);
      }
      if (typeof value === 'number' || typeof value === 'boolean') {
        return value.toString().toLowerCase().includes(searchTermLower);
      }
      if (Array.isArray(value)) {
        // If we're searching children and searchOnlyPrincipalData is true, don't search arrays
        if (searchOnlyPrincipalData && isSearchingChildren) {
          return false;
        }
        return value.some((item) => searchInValue(item, true));
      }
      if (typeof value === 'object') {
        // If we're searching children and searchOnlyPrincipalData is true, don't search objects
        if (searchOnlyPrincipalData && isSearchingChildren) {
          return false;
        }
        return Object.entries(value)
          .filter(([key]) => !excludedProperties.includes(key))
          .some(([, val]) => searchInValue(val, true));
      }
      return false;
    };

    const searchInRow = (row: any): boolean => {
      // If searchOnlyPrincipalData is true, exclude children from search
      const propertiesToSearch = searchOnlyPrincipalData 
        ? Object.entries(row).filter(([key]) => key !== childrenColumnName)
        : Object.entries(row);
      
      return propertiesToSearch
        .filter(([key]) => !excludedProperties.includes(key))
        .some(([, value]) => searchInValue(value, false));
    };

    if (searchOnlyPrincipalData) {
      // When searching only principal data, we want to:
      // 1. Filter parent rows based on their own data (excluding children)
      // 2. Keep the children structure intact for matching parents
      const matchingParents = data.filter((row) => searchInRow(row));
      
      // If no parents match, return empty array
      if (matchingParents.length === 0) {
        return [];
      }
      
      // If all parents match, return original data structure
      if (matchingParents.length === data.length) {
        return data;
      }
      
      // Return only the matching parents with their children intact
      return matchingParents;
    } else {
      // Search in all data including children
      return data.filter((row) => searchInRow(row));
    }

  }, [data, searchTerm, isMatrix, searchOnlyPrincipalData, childrenColumnName]);
};

export default useFilteredData;
